<template>
  <div 
    :class="cardContainerClasses"
    @click="$emit('click', card)"
    :style="{ width: cardWidth, height: cardHeight }"
  >
    <!-- Yu-Gi-Oh Card -->
    <div class="yugioh-card-simple relative bg-gray-900 rounded-lg shadow-xl">
      
      <!-- Method 1: Local complete card + rarity foil -->
      <div v-if="!faceDown && getYGOProCompleteImage()" class="complete-card-container">
        <CardArt
          :card-id="card.id"
          :foil="card.foil"
          :alt="localizedName(card.id, card.name)"
          :intensity="size === 'tiny' || size === 'small' ? 'thumb' : 'full'"
          @error="handleYGOProImageError"
        />
      </div>

      <!-- Method 2: Card Back (Face-down cards) -->
      <div v-else-if="faceDown" class="card-back-container">
        <img 
          :src="getCardBackImage()"
          :alt="'Card back'"
          class="complete-card-image w-full h-full object-cover rounded-lg"
          @error="handleCardBackError"
        />
      </div>

      <!-- Method 3: Simple Placeholder (Fallback) -->
      <div v-else class="placeholder-card-container">
        <div class="w-full h-full flex flex-col items-center justify-center p-4 text-center">
          <!-- Card Type Icon -->
          <div class="text-6xl mb-4">{{ getTypeIcon(card.type) }}</div>
          
          <!-- Card Name -->
          <h3 class="text-white font-bold text-lg mb-2 leading-tight">
            {{ localizedName(card.id, card.name) }}
          </h3>
          
          <!-- Card Type -->
          <p class="text-gray-300 text-sm mb-2">{{ t(`card.type.${card.type}`) }}</p>
          
          <!-- Monster Stats -->
          <div v-if="card.type === 'monster'" class="text-yellow-400 text-sm">
            <div>ATK: {{ card.attack || '?' }} / DEF: {{ card.defense || '?' }}</div>
            <div v-if="card.level">Level: {{ card.level }}</div>
          </div>
          
          <!-- Set Code -->
          <div class="text-xs text-gray-400 mt-2">{{ card.set_code || card.card_set }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import type { Card } from '../../../shared/src/types'
import { currentSleeveUrl, getLocalCardImageUrl } from '../utils/cardImages'
import { localizedName } from '../i18n/localizeCard'
import { useI18n } from '../i18n/t'
import CardArt from './CardArt.vue'

const props = withDefaults(defineProps<{
  card: any;
  clickable?: boolean;
  size?: 'tiny' | 'small' | 'medium' | 'large';
  faceDown?: boolean;
  /** Fill the parent instead of the fixed size for `size`. */
  fluid?: boolean;
}>(), {
  clickable: true,
  size: 'medium',
  faceDown: false,
  fluid: false,
})

defineEmits<{
  click: [card: Card]
}>()

const { t } = useI18n()
const ygoProImageError = ref(false)
const cardBackError = ref(false)

watch(
  () => props.card?.id,
  () => {
    ygoProImageError.value = false
  },
)

// Computed properties
const cardWidth = computed(() => {
  if (props.fluid) return '100%'
  switch (props.size) {
    case 'tiny': return '80px'
    case 'small': return '120px'
    case 'medium': return '180px'
    case 'large': return '240px'
    default: return '180px'
  }
})

const cardHeight = computed(() => {
  if (props.fluid) return '100%'
  switch (props.size) {
    case 'tiny': return '117px'
    case 'small': return '175px'
    case 'medium': return '263px'
    case 'large': return '350px'
    default: return '263px'
  }
})

const cardContainerClasses = computed(() => [
  'yugioh-card-container transition-all duration-300 ease-out',
  {
    'cursor-pointer hover:scale-105 hover:z-40': props.clickable,
    'hover:rotate-1': props.clickable && props.size !== 'small'
  }
])

// Method 1: Local card images (Primary method)
const getYGOProCompleteImage = () => {
  if (ygoProImageError.value) return '';
  const cardId = props.card.id;
  if (!cardId) return props.card.card_images?.[0]?.image_url || '';
  return getLocalCardImageUrl(cardId);
}

const handleYGOProImageError = () => {
  console.log(`YGOPro image not found for ${props.card.name} (passcode: ${props.card.passcode}), using placeholder`)
  ygoProImageError.value = true
}

// Method 2: Card back for face-down cards
const getCardBackImage = () => {
  if (cardBackError.value) return ''
  return currentSleeveUrl()
}

const handleCardBackError = () => {
  console.warn('Card back image failed to load')
  cardBackError.value = true
}

// Utility methods
const getTypeIcon = (type: string) => {
  switch (type) {
    case 'monster': return '👹'
    case 'spell': return '⚡'
    case 'trap': return '🔮'
    default: return '🃏'
  }
}
</script>

<style scoped>
.yugioh-card-simple {
  width: 100%;
  height: 100%;
  border-radius: 6px;
  background: #2a2a2a;
  overflow: visible;
}

/* Complete Card Image Styles */
.complete-card-container,
.card-back-container {
  width: 100%;
  height: 100%;
  border-radius: 6px;
}

.complete-card-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Placeholder Card Styles */
.placeholder-card-container {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #1f2937 0%, #374151 50%, #1f2937 100%);
  border: 2px solid #4b5563;
}

/* Hover effects */
.yugioh-card-container:hover .yugioh-card-simple {
  box-shadow: 
    0 25px 50px -12px rgba(0, 0, 0, 0.4);
}

/* Size adjustments */
.yugioh-card-container[style*="width: 80px"] .complete-card-image {
  border-radius: 4px;
}

.yugioh-card-container[style*="width: 120px"] .complete-card-image {
  border-radius: 6px;
}

.yugioh-card-container[style*="width: 180px"] .complete-card-image {
  border-radius: 6px;
}

.yugioh-card-container[style*="width: 240px"] .complete-card-image {
  border-radius: 6px;
}
</style> 